<template>
  <div>
    <div data-cy="form-label" class="b-dialog__top-panel mb-4">
      <h2>{{$t('CONTENT.contract.contract_editing')}}</h2>
      <button class="b-btn b-btn_border_none" @click="closeContractEditingDialog">
        <dynamic-icon icon="close" />
      </button>
    </div>
    <form class="b-form b-dialog__form">
      <server-error :error="error" />
      <div class="row px-0">
        <div class="col-4">
          <label for="number">{{$t('CONTENT.contract.contract_number')}}:<span class="b-form__required"> *</span></label>
          <input
            data-cy="contract-number"
            type="text"
            id="number"
            class="b-input"
            name="number"
            required
            v-model.lazy="formData.number"
          />
        </div>
        <div class="col-4">
          <label>{{$t('CONTENT.contract.contract_date')}}:<span class="b-form__required"> *</span></label>
          <datepicker v-bind:class="{ 'b-datepicker__error': isFieldWithError('date', date) }" class="b-datepicker" v-model="date" :formatter="datepickerFormat"></datepicker>
        </div>
        <div class="col-4">
          <label for="externalId">{{$t('CONTENT.external_id')}}:</label>
          <input
            data-cy="contract-externalId"
            type="text"
            id="externalId"
            class="b-input"
            name="externalId"
            v-model.lazy="formData.externalId"
          />
        </div>
      </div>
      <div class="row px-0 mt-5">
        <div class="col-4">
          <span class="text-bold">{{$t('CONTENT.contract.imported')}}</span>
        </div>
        <div class="col-4">
          <span class="text-bold">{{$t('CONTENT.contract.lat_create_date')}}:</span>
        </div>
      </div>
      <div class="row px-0">
        <div class="col-4">
          <span class="text-bold">{{$t('CONTENT.contract.status_text')}} {{this.getContractStatus()}}</span>
        </div>
        <div class="col-4">
          <datepicker
            v-model="createDate"
            :class="{ 'b-datepicker__error': isFieldWithError('date', createDate) }"
            class="b-datepicker"
            :formatter="datepickerFormat"
          />
        </div>
      </div>
      <div class="b-form__container">
        <label>{{$t('CONTENT.contract.lessee')}}:<span class="b-form__required"> *</span></label>
        <cool-select :list="counterparties"
                     option-value="id"
                     option-text="value"
                     :isError="isFieldWithError('currentLessee', currentLessee)"
                     :placeholder="$t('CONTENT.choose')"
                     v-model="currentLessee"
        />
      </div>

      <div class="b-contract__check mx-2 mb-2" v-if="checkIsCounterpartyIntragroup(currentLessee)">
        <dynamic-icon class="mr-2" icon="check"></dynamic-icon>
        {{$t('CONTENT.counterparty.VGO')}}
      </div>

      <div class="row mt-5">
        <div class="col-3">
          <div class="b-form__container">
            <label>{{$t('CONTENT.contract.lessor')}}:<span class="b-form__required"> *</span></label>
          </div>
        </div>
      </div>
      <div :key="counter" class="b-contracts__lessor-wrap">
        <div v-for="(item, index) in formData.lessorCounterpartyIds" :key="index" class="b-contracts__lessor-item mb-5">
          <div class="row px-0 b-form__container_type_flex">
            <div class="col-12">
              <LessorItemEdit :lessor="item"
                              :lessorsLength="formData.lessorCounterpartyIds.length"
                              :lessorIndex="index"
                              :isError="formData.lessorCounterpartyIds && formData.lessorCounterpartyIds.length - 1 === index ? isFieldWithError('lessor', item) : false"
                              @deleteLessor="deleteLessorItem"
                              @selectLessor="onSelectLessor($event)"/>
            </div>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-8">
          <div class="b-form__container">
            <button type="button" @click="addLessor" class="b-btn" data-cy="add-lessor" :disabled="isDisabledAddLessor">
              <dynamic-icon class="b-btn__icon b-btn__icon_color_main-text" icon="plus"></dynamic-icon>
              {{$t('CONTENT.contract.add_lessor')}}
            </button>
          </div>
        </div>
      </div>
      <div class="row mt-5 b-form__row_type_flex-end">
        <div class="col-4">
          <div class="b-form__container">
            <label>{{$t('CONTENT.contract.turnover_percentage')}}:</label>
            <numeric class="b-numeric" controls :min="0" :max="100" v-model="formData.turnoverPercentage"></numeric>
          </div>
        </div>
        <div class="col-4">
          <div class="b-form__container">
            <label>{{$t('CONTENT.contract.revenues')}}:</label>
            <numeric class="b-numeric" :min="0" controls v-model="formData.revenues"></numeric>
          </div>
        </div>
      </div>
      <div class="row mt-5 px-0">
        <div class="col-12">
          <label for="commentary">{{$t('CONTENT.comment')}}:</label>
          <textarea
            data-cy="contract-externalId"
            type="text"
            id="commentary"
            class="b-textarea"
            name="externalId"
            v-model.lazy="formData.commentary"
          />
        </div>
      </div>
      <div class="row px-0">
        <div class="col-4">
          <label for="responsible">{{$t('CONTENT.contract.initiator')}}:<span class="b-form__required"> </span></label>
          <input
            data-cy="contract-number"
            type="text"
            id="responsible"
            class="b-input"
            name="responsible"
            required
            v-model.lazy="formData.responsible"
          />
        </div>
      </div>
      <div class="row px-0">
        <div class="col-4">
          <label for="responsible">{{$t('CONTENT.contract.prolongation_type')}}:<span class="b-form__required"> </span></label>
          <input
            data-cy="contract-number"
            type="text"
            id="responsible"
            class="b-input"
            name="responsible"
            required
            v-model.lazy="formData.prolongationType"
          />
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-8 pt-1">
          <span v-if="isShowWarning" class="b-contract__text-warning">Внимание! При удалении контрагента будут удалены все его платежи в активах.
            Проверьте платежи по оставшимся контрагентам.</span>
        </div>
        <div class="col-4">
          <div
            class="b-form__container b-form__container_type_flex b-form__container_type_flex_end"
          >
            <button
              data-cy="save-button"
              class="b-btn b-btn_type_primary"
              type="submit"
              @click="onSubmit"
            >{{$t('CONTENT.save')}}</button>
            <button
              class="b-btn b-btn_type_empty m-2"
              @click.prevent="closeContractEditingDialog"
            >{{$t('CONTENT.cancel')}}</button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GeneralForm from "../../../../directories/counterparty/edit/GeneralForm";
import ImportDialogWidget from "../../../../widgets/ImportDialogWidget";
import dateConverter from "../../../../../utils/converters/date.converter";
import LessorItemEdit from "./LessorItemEdit";
import datepickerFormat from '@/mixins/datepickerFormat.mixin';

export default {
  components: {
    LessorItemEdit,
    GeneralForm,
    ImportDialogWidget
  },
  mixins: [datepickerFormat],
  props: {
    contract: {
      type: Object
    },
  },
  data() {
    return {
      error: null,
      counter: 0,
      isContractEditingShow: false,
      isDisabledAddLessor: false,
      isShowWarning: false,
      validationErrors: [],
      formData: {},
      contractStatuses: {
        5: `${this.$t('CONTENT.contract_status.status_active')}`,
        4: `${this.$t('CONTENT.contract_status.status_on_approval_modified')}`,
        3: `${this.$t('CONTENT.contract_status.status_modified')}`,
        2: `${this.$t('CONTENT.contract_status.status_on_approval_create')}`,
        1: `${this.$t('CONTENT.contract_status.status_project')}`,
      },
    };
  },
  created() {
    this.updateFormData();
  },
  watch: {
    contract: {
      immediate: true,
      handler() {
        this.updateFormData();
      }
    },
  },
  computed: {
    ...mapGetters(["getCounterpartiresForContract"]),
    ...mapGetters(["allCounterparties"]),
    date: {
      get() {
        if (this.formData.date === null) {
          return null;
        } else {
          return dateConverter.convertToDate(this.formData.date);
        }
      },
      set(newVal) {
        if(newVal !== null) {
          this.formData.date = dateConverter.convertToString(newVal);
        } else {
          this.formData.date = null;
        }
      }
    },
    createDate: {
      get() {
        if (this.formData.createDate === null || this.formData.createDate === "") {
          return null;
        } else {
          return dateConverter.convertToDate(this.formData.createDate);
        }
      },
      set(newVal) {
        if(newVal !== null) {
          this.formData.createDate = dateConverter.convertToString(newVal);
        } else {
          this.formData.createDate = null;
        }
      }
    },
    counterparties() {
      return this.getCounterpartiresForContract;
    },
    currentLessee: {
      get() {
        return this.formData.lesseeCounterpartyId;
      },
      set(newVal) {
        this.formData.lesseeCounterpartyId = +newVal;
      }
    },
  },
  methods: {
    getContractStatus() {
      return this.contractStatuses[this.contract.status] ? this.contractStatuses[this.contract.status] : '';
    },
    updateFormData() {
      if (this.contract != null) {
        this.formData = _.cloneDeep (this.contract);
      }
    },
    addLessor() {
      this.formData.lessorCounterpartyIds.push(null);
      this.isDisabledAddLessor = true;
    },
    onSelectLessor(data) {
      if (this.formData.lessorCounterpartyIds[data.index] !== null) {
        this.$store.commit('ADD_RENAME_LESSOR_LIST', { value: data.value, oldIndex: data.index, oldValue: data.oldValue });
      }

      this.formData.lessorCounterpartyIds[data.index] = data.value;
      this.isDisabledAddLessor = false;
    },
    closeContractEditingDialog() {
      this.$store.commit('CLEAR_RENAME_LESSOR_LIST');
      this.$emit("close");
      this.formData = _.cloneDeep(this.contract);
    },
    deleteLessorItem(index) {
      this.isShowWarning = true;
      this.formData.lessorCounterpartyIds.splice(index, 1);
      this.isDisabledAddLessor = false;
      this.counter++;
    },
    onSubmit(e) {
      e.preventDefault();
      if (!this.validate()) return;
      this.$emit('onSaveEditing', this.formData);
      this.$emit("close");
      this.formData = _.cloneDeep(this.contract);
    },
    checkIsCounterpartyIntragroup(counterpartyId) {
      for (let i = 0; i < this.allCounterparties.length; i++) {
        if (this.allCounterparties[i].idInDB === counterpartyId) {
          return this.allCounterparties[i].isIntragroupTurnover;
        }
      }
    },
    validate() {
      this.validationErrors = [];
      if (!this.formData.number) {
        this.validationErrors.push('number');
      }
      if (!this.formData.lesseeCounterpartyId) {
        this.validationErrors.push('currentLessee');
      }
      if (!this.formData.date) {
        this.validationErrors.push('date');
      }
      if (!this.arrayIsNotEmpty(this.formData.lessorCounterpartyIds)) {
        this.validationErrors.push('lessor');
      }
      return this.validationErrors.length <= 0;
    },
    isFieldWithError(field, value) {
      return !value && this.validationErrors && this.validationErrors.includes(field);
    },
    arrayIsNotEmpty(array) {
      return Array.isArray(array) && array.length > 0 && array.every(a => !!a);
    }
  }
};
</script>
